import type { Vec2 } from '../geo/types';
import type { TourChapter, TourId } from './types';
import { CITY_SOURCES as SOURCES } from '../history/sources';
import { CITY_INTRODUCTION } from '../history/cityIntroduction';

type Stop = Omit<TourChapter, 'at'> & { readonly point?: Vec2 };

const WALK_STOPS: readonly Stop[] = [
  {
    title: 'The medieval town hall',
    text: 'The narrow gabled building is the old Stadhuis. Anthonisz painted it beside the Dam in 1538; the present palace belongs to a later city.',
    source: SOURCES.map.url,
  },
  {
    point: [0, 28],
    title: 'A city held between two churches',
    text: 'The Nieuwe Kerk rises beyond the square. Churches, convents and narrow house plots shape this enclosed city, long before the seventeenth-century canal belt.',
    source: SOURCES.map.url,
  },
  {
    point: [30, 23],
    title: 'The Dam joins the city',
    text: 'This broad crossing grew from the dam connecting the settlements on either bank of the Amstel. Trade and civic buildings gathered around it.',
    source: SOURCES.dam.url,
  },
  {
    point: [53.9, -14.1],
    title: 'Fish arrives at the market',
    text: 'The fish market stood on the Dam from medieval times. Covered selling benches linked the square to boats bringing their catch along the Damrak.',
    source: SOURCES.fish.url,
  },
  {
    point: [60, -36],
    title: 'Water beneath the street',
    text: 'The Dam separates the Damrak from the Rokin. The crossing, sluice and adjoining buildings made water management part of everyday life in the centre.',
    source: SOURCES.sluice.url,
  },
];

const BOAT_STOPS: readonly Stop[] = [
  {
    title: 'From the IJ into the town',
    text: 'Large seagoing ships worked at the IJ; smaller inland craft filled the Damrak. We follow that inner harbour towards the Dam.',
    source: SOURCES.harbour.url,
  },
  {
    point: [350, 330],
    title: 'A warehouse door on the water',
    text: 'Warmoesstraat houses faced the street, with storage buildings behind them on the Damrak. Boats could reach those rear doors directly.',
    source: SOURCES.bridge.url,
  },
  {
    point: [154, 100],
    title: 'The narrower western quay',
    text: 'The western bank was a narrow working quay, known as Op ’t Water. The broad modern street and its traffic are absent from this harbour.',
    source: SOURCES.bridge.url,
  },
  {
    point: [79, -1],
    title: 'The fish market from below',
    text: 'Above the water, the Dam carried the fish market. The boats, covered benches and crossing belonged to one working place.',
    source: SOURCES.fish.url,
  },
  {
    point: [384, 479],
    title: 'Back towards the open harbour',
    text: 'Beyond the Nieuwebrug lies the IJ. Its place at the mouth of the Damrak connects the enclosed town to routes beyond its walls.',
    source: SOURCES.map.url,
  },
];

const FLY_STOPS: readonly TourChapter[] = [
  {
    at: 0,
    title: 'A city becoming a world harbour',
    text: CITY_INTRODUCTION.en,
    source: SOURCES.harbour.url,
  },
  {
    at: 0.08,
    title: 'Anthonisz’s Amsterdam',
    text: 'Cornelis Anthonisz painted the town from above the IJ in 1538. This reconstruction follows its streets and major buildings; hidden plans and exact dimensions remain estimates.',
    source: SOURCES.map.url,
  },
  {
    at: 0.18,
    title: 'The harbour and the Oude Kerk',
    text: 'The harbour occupies the foreground of the painting. The Oude Kerk rises among dense houses behind it, within the medieval wall circuit.',
    source: SOURCES.map.url,
  },
  {
    at: 0.34,
    title: 'The Dam at the centre',
    text: 'The Dam connects the two banks of the Amstel. The medieval town hall, trading places and the two stretches of river meet here.',
    source: SOURCES.dam.url,
  },
  {
    at: 0.5,
    title: 'An enclosed town',
    text: 'Singel, Geldersekade and Kloveniersburgwal mark the medieval perimeter. The later canal belt has not yet been laid out.',
    source: SOURCES.map.url,
  },
  {
    at: 0.74,
    title: 'A port before a world city',
    text: 'The Damrak is an inner harbour rather than a boulevard. Inland boats and water-facing storage buildings show how trade shaped the town.',
    source: SOURCES.harbour.url,
  },
];

export const TOUR_SUMMARIES: Readonly<Record<TourId, string>> = {
  churches:
    'Nine churches and chapels: sailors, pilgrims, beguines and the people who cared for the city.',
  walk: 'Town hall, fish market and sluice: everyday life around the Dam.',
  boat: 'Follow the working harbour past quays and water-facing warehouses.',
  flyover: 'Read the walled city through Anthonisz’s bird’s-eye view.',
};

/*
 * Captions follow actual route chainage so a bend or route edit cannot detach
 * the story from the place it describes.
 */
export function tourChapters(
  id: Exclude<TourId, 'churches'>,
  path: readonly Vec2[]
): readonly TourChapter[] {
  if (id === 'flyover') return FLY_STOPS;
  let previous = path[0],
    length = 0;
  if (!previous) throw new Error('A narrated tour requires a path');
  const stations = path.map((point) => {
    if (previous) length += Math.hypot(point[0] - previous[0], point[1] - previous[1]);
    previous = point;
    return { point, distance: length };
  });
  const first = stations[0];
  if (!first || length <= 0) throw new Error('A narrated tour requires a nonzero path');
  return (id === 'walk' ? WALK_STOPS : BOAT_STOPS)
    .map(({ point, ...stop }): TourChapter => {
      const away = (at: typeof first) =>
        point ? Math.hypot(at.point[0] - point[0], at.point[1] - point[1]) : 0;
      const nearest = stations.reduce((near, at) => (away(at) < away(near) ? at : near), first);
      return { ...stop, at: nearest.distance / length };
    })
    .sort((a, b) => a.at - b.at);
}
